import { READINESS_EXIT_DURATION_MS } from 'modules/overlay-v2/domain/constants/readiness';
import { OverlayReadinessSignals, OverlayReadinessState } from 'modules/overlay-v2/domain/interfaces/readiness';
import { buildReadinessSteps } from 'modules/overlay-v2/domain/utils/build-readiness-steps';
import { selectGameInfoError } from 'modules/overlay-v2/store/game-info/selectors';
import { selectIsBroadcastInitialized, selectIsMatchAvailable } from 'modules/overlay-v2/store/overlay/selectors';
import { retryCancelled, retryRequested, retryScheduled } from 'modules/overlay-v2/store/readiness/readiness';
import {
  selectAssetsTask,
  selectChronologyTask,
  selectDeviceTask,
  selectMatchTimeTask,
  selectReadinessRetry,
  selectRetryAt,
  selectRetryToken,
  selectTemplateTask,
} from 'modules/overlay-v2/store/readiness/selectors';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import useOverlayRouteParams from 'modules/overlay-v2/ui/hooks/use-overlay-route-params';
import useRemoteConnectionStep from 'modules/overlay-v2/ui/hooks/use-remote-connection-step';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { useAppDispatch, useAppSelector } from 'store/hooks';

export interface OverlayReadiness extends OverlayReadinessState {
  /** Повторить подготовку прямо сейчас, не дожидаясь автоматического повтора. */
  onRetry: () => void;
  /**
   * Показывать ли экран подготовки. Держится ещё `READINESS_EXIT_DURATION_MS` после
   * готовности, чтобы чеклист успел показать «Всё готово» и уйти анимацией,
   * а не сменился табло рывком.
   */
  isScreenVisible: boolean;
}

/**
 * Чеклист подготовки трансляции для оверлея и панели управления.
 *
 * Хук только собирает сигналы из тех мест, где они уже живут, — саму лестницу считает
 * чистая `buildReadinessSteps`. Своё состояние здесь минимально: три шага, которым
 * негде было храниться (журнал матча, время, картинки), лежат в слайсе `readiness`.
 */
function useOverlayReadiness(): OverlayReadiness {
  const dispatch = useAppDispatch();

  const { organizerId, error: routeError } = useOverlayRouteParams();

  const {
    data: feature,
    isLoading: isFeatureLoading,
    error: featureError,
    refetch: refetchFeature,
  } = useFetchAvailableFeature({ userTournamentId: organizerId });

  const isBroadcastInitialized = useAppSelector(selectIsBroadcastInitialized);
  const isMatchAvailable = useAppSelector(selectIsMatchAvailable);

  const device = useAppSelector(selectDeviceTask);
  const chronology = useAppSelector(selectChronologyTask);
  const matchTime = useAppSelector(selectMatchTimeTask);
  const assets = useAppSelector(selectAssetsTask);
  const template = useAppSelector(selectTemplateTask);
  const retry = useAppSelector(selectReadinessRetry);
  const retryToken = useAppSelector(selectRetryToken);

  const { championship, teams, isSynchronization, isSynchronize } = useGameInfo();
  const gameInfoError = useAppSelector(selectGameInfoError);
  const { activeTemplate, isLoaded: isTemplateLoaded } = useTemplates();
  const { boards } = useBoards();

  const remote = useRemoteConnectionStep(isMatchAvailable === true);

  const involvedPlayers = useMemo(() => {
    if (!teams) return undefined;
    const count = (members?: { isInvolved: boolean }[]) => members?.filter((member) => member.isInvolved).length ?? 0;
    return { first: count(teams.first.members), second: count(teams.second.members) };
  }, [teams]);

  const signals: OverlayReadinessSignals = useMemo(
    () => ({
      routeError,
      subscription: {
        isLoading: isFeatureLoading,
        error: featureError ? ((featureError as Error).message ?? String(featureError)) : undefined,
        hasObsIntegration: Boolean(feature?.OBS_INTEGRATION),
      },
      isBroadcastInitialized,
      device,
      isMatchAvailable,
      matchData: { isLoading: isSynchronization, isLoaded: isSynchronize, error: gameInfoError },
      involvedPlayers,
      chronology,
      matchTime,
      assets,
      template,
      championship: championship ? { name: championship.name, hasTemplate: Boolean(championship.overlayId) } : undefined,
      isTemplateLoaded,
      templateName: activeTemplate?.name,
      boardsCount: boards.length,
      remote,
      teamNames: teams ? { first: teams.first.name, second: teams.second.name } : undefined,
      retry,
    }),
    [
      routeError,
      isFeatureLoading,
      featureError,
      feature?.OBS_INTEGRATION,
      isBroadcastInitialized,
      device,
      isMatchAvailable,
      isSynchronization,
      isSynchronize,
      gameInfoError,
      involvedPlayers,
      chronology,
      matchTime,
      assets,
      template,
      championship,
      isTemplateLoaded,
      activeTemplate?.name,
      boards.length,
      remote,
      teams,
      retry,
    ],
  );

  const onRetry = useCallback(() => dispatch(retryRequested()), [dispatch]);

  const state = useMemo(() => buildReadinessSteps(signals), [signals]);

  // Подписка живёт в react-query с бессрочным кэшем (`staleTime: Infinity, retry: false`),
  // поэтому её сбой сам не рассасывается: без явного `refetch` шаг оставался красным
  // навсегда, даже когда сеть уже вернулась и все запросы отвечали 200.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    refetchFeature();
  }, [retryToken, refetchFeature]);

  useRetryDriver(state, useAppSelector(selectRetryAt));

  const isScreenVisible = useExitDelay(state.isReady);

  return useMemo(() => ({ ...state, onRetry, isScreenVisible }), [state, onRetry, isScreenVisible]);
}

/**
 * Автоповтор упавшего шага: назначает попытку, ведёт её по таймеру и снимает, когда шаг
 * починился сам. Живёт рядом с чеклистом, а не в загрузчике, потому что упасть может
 * что угодно — подписка, оформление, данные матча, — а видит их все только чеклист.
 */
function useRetryDriver(state: OverlayReadinessState, retryAt: number | undefined) {
  const dispatch = useAppDispatch();

  const failedStep = state.steps.find((step) => step.status === 'failed');
  const isRetryNeeded = Boolean(failedStep?.isRetryable);

  useEffect(() => {
    if (!isRetryNeeded) {
      if (retryAt !== undefined) dispatch(retryCancelled());
      return;
    }

    if (retryAt === undefined) {
      dispatch(retryScheduled({ now: Date.now() }));
      return;
    }

    const timer = setTimeout(() => dispatch(retryRequested()), Math.max(0, retryAt - Date.now()));
    return () => clearTimeout(timer);
  }, [dispatch, isRetryNeeded, retryAt]);
}

/** Держит экран подготовки ещё немного после готовности — на «Всё готово» и уход анимацией. */
function useExitDelay(isReady: boolean): boolean {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!isReady) {
      setIsVisible(true);
      return;
    }

    const timer = setTimeout(() => setIsVisible(false), READINESS_EXIT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isReady]);

  return isVisible;
}

export default useOverlayReadiness;
